import type {
  InventoryVehicle,
  InventoryFilters,
  PrCodesResponse,
  FilterOption,
} from './dto.inventory';
import { formatPrice as formatLegoPrice } from '@lucid/lego';

/*
|--------------------------------------------------------------------------
| TRANSFORMED TYPES
|--------------------------------------------------------------------------
*/

/**
 * Transformed inventory vehicle for table display
 */
export type TransformedVehicle = {
  id: string;
  vin: string;
  webLink: string;
  batchNumber: string;
  availability: string;
  model: string;
  modelCode: string;
  trim: string;
  trimCode: string;
  drivetrain: string;
  drivetrainCode: string;
  exteriorColor: string;
  exteriorColorCode: string;
  interiorColor: string;
  interiorColorCode: string;
  appearance: string;
  appearanceCode: string;
  roof: string;
  roofCode: string;
  wheels: string;
  wheelsCode: string;
  year: string;
  adas: string;
  adasCode: string;
  soundSystem: string;
  soundSystemCode: string;
  frontSeats: string;
  frontSeatsCode: string;
  rearSeats: string;
  packages: string;
  packagesCode: string;
  location: {
    city: string;
    state: string;
    zipcode: string;
    distance: number;
    locationType: string;
  };
  pricing: {
    baseMSRP: number;
    totalMSRP: number;
    destinationCharge: number;
    finalPrice: number;
  };
  status: {
    isInTransit: boolean;
    isAtStudio: boolean;
    isAtHub: boolean;
  };
};

/**
 * Parsed filter options grouped by category
 */
export type ParsedFilterOptions = {
  exteriorColor: FilterOption[];
  interiorTheme: FilterOption[];
  wheels: FilterOption[];
  appearance: FilterOption[];
  trim: FilterOption[];
  driverAssistance: FilterOption[];
  soundSystem: FilterOption[];
  seating: FilterOption[];
  /** LUCID_PACKAGES typeId — comfort/convenience packages (e.g. CCP05 for Gravity) */
  packages: FilterOption[];
  /** VC_LCD_DYPKG typeId — dynamic handling package options (e.g. DHP00, DHP01) */
  dynamicHandling: FilterOption[];
  /** VC_LCD_TECPKG typeId — technology package options (e.g. TNP00, TNP01, TNP02) */
  technology: FilterOption[];
  /** VC_LCD_TPKG typeId — towing package options (e.g. TP00, TP01, TP02) */
  towing: FilterOption[];
  modelYear: FilterOption[];
  drivetrain: FilterOption[];
  roof: FilterOption[];
  model: FilterOption[];
  modelVariant: FilterOption[];
};

/*
|--------------------------------------------------------------------------
| VEHICLE TRANSFORMERS
|--------------------------------------------------------------------------
*/

/**
 * Transform a single inventory vehicle from API response to display format
 * @param vehicle - Raw inventory vehicle from API
 * @returns Transformed vehicle data ready for UI consumption
 *
 * @example
 * const vehicle = transformVehicle(apiVehicle);
 * // vehicle.exteriorColor, vehicle.pricing.totalMSRP, etc.
 */
export const transformVehicle = (vehicle: InventoryVehicle): TransformedVehicle => ({
  id: vehicle.uuid,
  vin: vehicle.VIN,
  webLink: vehicle.shortcode,
  batchNumber: vehicle.batchNumber,
  availability: vehicle.availability,
  model: vehicle.model?.technicalName || '',
  modelCode: vehicle.model?.sapCode || '',
  trim: vehicle.modelVariant?.technicalName || '',
  trimCode: vehicle.modelVariant?.sapCode || '',
  drivetrain: vehicle.motorDrive?.technicalName || '',
  drivetrainCode: vehicle.motorDrive?.sapCode || '',
  exteriorColor: vehicle.color?.technicalName || '',
  exteriorColorCode: vehicle.color?.sapCode || '',
  interiorColor: vehicle.interior?.technicalName || '',
  interiorColorCode: vehicle.interior?.sapCode || '',
  appearance: vehicle.appearance?.technicalName || '',
  appearanceCode: vehicle.look?.sapCode || '',
  roof: vehicle.roof?.technicalName || '',
  roofCode: vehicle.roof?.sapCode || '',
  wheels: vehicle.wheels?.technicalName || '',
  wheelsCode: vehicle.wheels?.sapCode || '',
  year: vehicle.modelYear,
  adas: vehicle.driverAssistance?.technicalName || '',
  adasCode: vehicle.driverAssistance?.sapCode || '',
  soundSystem: vehicle.soundSystem?.technicalName || '',
  soundSystemCode: vehicle.soundSystem?.sapCode || '',
  frontSeats: vehicle.seating?.technicalName || '',
  frontSeatsCode: vehicle.seating?.sapCode || '',
  rearSeats: vehicle.rearSeats?.backSeats || '',
  packages: vehicle.packages?.technicalName || '',
  packagesCode: vehicle.packages?.sapCode || '',
  location: {
    city: vehicle.address?.city || '',
    state: vehicle.address?.state || '',
    zipcode: vehicle.address?.zipcode || '',
    distance: vehicle.distance || 0,
    locationType: vehicle.address?.locationType || '',
  },
  pricing: {
    baseMSRP: vehicle.pricing?.baseMSRP || 0,
    totalMSRP: vehicle.pricing?.totalMSRP || 0,
    destinationCharge: vehicle.pricing?.destinationCharge || 0,
    finalPrice: vehicle.pricing?.finalPrice || 0,
  },
  status: {
    isInTransit: vehicle.isInTransit,
    isAtStudio: vehicle.isAtStudio,
    isAtHub: vehicle.isAtHub,
  },
});

/**
 * Transform array of inventory vehicles from API response
 * @param vehicles - Array of raw inventory vehicles from API
 * @returns Array of transformed vehicles ready for UI consumption
 *
 * @example
 * const vehicles = transformVehicles(apiResponse.vehicleData);
 */
export const transformVehicles = (vehicles: InventoryVehicle[]): TransformedVehicle[] =>
  vehicles.map(transformVehicle);

/*
|--------------------------------------------------------------------------
| FILTER TRANSFORMERS
|--------------------------------------------------------------------------
*/

/**
 * Parse prCodes API response into grouped filter options
 * @param data - Raw prCodes response from CPQ API
 * @returns Filter options grouped by category with trim availability
 *
 * @example
 * const filterOptions = transformPrCodes(prCodesResponse);
 * // filterOptions.exteriorColor, filterOptions.wheels, etc.
 */
export const transformPrCodes = (data: PrCodesResponse): ParsedFilterOptions => {
  const result: ParsedFilterOptions = {
    exteriorColor: [],
    interiorTheme: [],
    wheels: [],
    appearance: [],
    trim: [],
    driverAssistance: [],
    soundSystem: [],
    seating: [],
    packages: [],
    dynamicHandling: [],
    technology: [],
    towing: [],
    modelYear: [],
    drivetrain: [],
    roof: [],
    model: [],
    modelVariant: [],
  };

  // Map API trim names to our filter trim codes
  // API returns lowercase snake_case: air (=Pure), touring, grand_touring, sapphire
  // Note: "air" in prCodes means Pure trim, "gravity" means base Gravity trim
  const trimNameToCode: Record<string, string> = {
    // API snake_case format (primary)
    air: 'PURE', // "air" in prCodes = Pure trim for Air model
    pure: 'PURE',
    touring: 'AT',
    grand_touring: 'GT',
    sapphire: 'SAPPHIRE',
    gravity: 'AT', // "gravity" in prCodes = base Gravity trim (Touring)
    // Title case / other formats (fallback)
    Pure: 'PURE',
    PURE: 'PURE',
    Touring: 'AT',
    AT: 'AT',
    'Air Touring': 'AT',
    'Grand Touring': 'GT',
    'grand touring': 'GT',
    GT: 'GT',
    Sapphire: 'SAPPHIRE',
    SAPPHIRE: 'SAPPHIRE',
    // Gravity variants
    'Gravity Touring': 'AT',
    'Gravity Grand Touring': 'GT',
    'Gravity Touring Plus': 'TP',
    'Gravity Grand Touring Ultimate': 'TU',
  };

  Object.entries(data).forEach(([code, item]) => {
    // Extract available trims from prices array and normalize to our trim codes
    const availableTrims =
      item.prices
        ?.map((p) => {
          const mappedTrim = trimNameToCode[p.trim];
          // If we don't recognize the trim, skip it to allow all trims
          return mappedTrim;
        })
        .filter((trim): trim is string => trim !== undefined)
        .filter((trim, index, self) => self.indexOf(trim) === index) || []; // Remove duplicates

    const option: FilterOption = {
      label: item.name || item.technicalName,
      value: code,
      image: item.image,
      availableTrims: availableTrims.length > 0 ? availableTrims : undefined,
    };

    switch (item.typeId) {
      case 'LUCID_EXT_COLOR':
        result.exteriorColor.push(option);
        break;
      case 'LUCID_INT_THEME':
        result.interiorTheme.push(option);
        break;
      case 'LUCID_WHEELS':
        result.wheels.push(option);
        break;
      case 'LUCID_APPEARANCE':
        result.appearance.push(option);
        break;
      case 'LUCID_TRIM':
        result.trim.push(option);
        break;
      case 'LUCID_DREAMDRIVE':
        result.driverAssistance.push(option);
        break;
      case 'LUCID_SOUNDSYSTEM':
        result.soundSystem.push(option);
        break;
      case 'LUCID_SEATING':
        result.seating.push(option);
        break;
      case 'LUCID_PACKAGES':
        result.packages.push(option);
        break;
      case 'VC_LCD_DYPKG':
        result.dynamicHandling.push(option);
        break;
      case 'VC_LCD_TECPKG':
        result.technology.push(option);
        break;
      case 'VC_LCD_TPKG':
        result.towing.push(option);
        break;
      case 'LUCID_MODEL_YEAR':
        result.modelYear.push(option);
        break;
      case 'LUCID_DRIVETRAIN':
        result.drivetrain.push(option);
        break;
      case 'LUCID_ROOF':
        result.roof.push(option);
        break;
      case 'LUCID_MODEL':
        result.model.push(option);
        break;
      case 'LUCID_MODEL_VARIANT':
        result.modelVariant.push(option);
        break;
    }
  });

  // Deduplicate options by label (same translated name = same filter option)
  const deduplicateByLabel = (options: FilterOption[]): FilterOption[] => {
    const seen = new Map<string, FilterOption>();
    for (const opt of options) {
      const existing = seen.get(opt.label);
      if (existing) {
        // Merge SAP code values so all codes for this label are sent to the API
        // (e.g. EXT05G and EXT07G both map to "Stealth Mit Glas")
        const existingCodes = existing.value.split(',');
        if (!existingCodes.includes(opt.value)) {
          existing.value = `${existing.value},${opt.value}`;
        }
        // Merge availableTrims from duplicate entries
        if (opt.availableTrims && existing.availableTrims) {
          existing.availableTrims = [
            ...new Set([...existing.availableTrims, ...opt.availableTrims]),
          ];
        } else if (opt.availableTrims) {
          existing.availableTrims = opt.availableTrims;
        }
      } else {
        seen.set(opt.label, { ...opt });
      }
    }
    return Array.from(seen.values());
  };

  // Deduplicate all filter option arrays
  Object.keys(result).forEach((key) => {
    result[key as keyof ParsedFilterOptions] = deduplicateByLabel(
      result[key as keyof ParsedFilterOptions]
    );
  });

  // Sort all options alphabetically by label
  const sortByLabel = (a: FilterOption, b: FilterOption) => a.label.localeCompare(b.label);
  Object.keys(result).forEach((key) => {
    result[key as keyof ParsedFilterOptions].sort(sortByLabel);
  });

  return result;
};

/*
|--------------------------------------------------------------------------
| CLIENT-SIDE FILTER FUNCTIONS
|--------------------------------------------------------------------------
*/

/**
 * Apply client-side filters to transformed vehicles
 * These filters are not supported by the API and must be applied after fetch
 * @param vehicles - Array of transformed vehicles
 * @param filters - Active filter values
 * @returns Filtered array of vehicles
 *
 * @example
 * const filtered = applyFilters(vehicles, { exteriorColor: ['L102'] });
 */
export const applyFilters = (
  vehicles: TransformedVehicle[],
  filters: InventoryFilters
): TransformedVehicle[] => {
  let result = [...vehicles];

  // Filter by exterior color
  if (filters.exteriorColor?.length) {
    result = result.filter((v) => filters.exteriorColor!.includes(v.exteriorColorCode));
  }

  // Filter by interior theme
  if (filters.interiorTheme?.length) {
    result = result.filter((v) => filters.interiorTheme!.includes(v.interiorColorCode));
  }

  // Filter by wheels
  if (filters.wheels?.length) {
    result = result.filter((v) => filters.wheels!.includes(v.wheelsCode));
  }

  // Filter by appearance
  if (filters.appearance?.length) {
    result = result.filter((v) => filters.appearance!.includes(v.appearanceCode));
  }

  // Filter by driver assistance
  if (filters.driverAssistance?.length) {
    result = result.filter((v) => filters.driverAssistance!.includes(v.adasCode));
  }

  // Filter by sound system
  if (filters.soundSystem?.length) {
    result = result.filter((v) => filters.soundSystem!.includes(v.soundSystemCode));
  }

  // Filter by seating
  if (filters.seating?.length) {
    result = result.filter((v) => filters.seating!.includes(v.frontSeatsCode));
  }

  // Filter by packages
  if (filters.packages?.length) {
    result = result.filter((v) => filters.packages!.includes(v.packagesCode));
  }

  return result;
};

/*
|--------------------------------------------------------------------------
| UTILITY FUNCTIONS
|--------------------------------------------------------------------------
*/

/**
 * Check if a filter option is available for the selected trims
 * @param option - Filter option to check
 * @param selectedTrims - Currently selected trim codes
 * @returns Whether the option is available
 *
 * @example
 * const isAvailable = isOptionAvailableForTrims(wheelOption, ['PURE', 'AT']);
 */
export const isOptionAvailableForTrims = (
  option: FilterOption,
  selectedTrims?: string[]
): boolean => {
  // If no availableTrims info, assume available for all
  if (!option.availableTrims?.length) {
    return true;
  }
  // If no trims selected, show all as available
  if (!selectedTrims?.length) {
    return true;
  }
  // Check if any selected trim matches the option's available trims
  return selectedTrims.some((trim) => option.availableTrims?.includes(trim));
};

/**
 * Get availability label from availability code
 * @param availability - Availability code from API
 * @returns Human-readable availability label
 */
export const getAvailabilityLabel = (availability: string): string => {
  const labels: Record<string, string> = {
    AVAILABLE_NOW: 'Available Now',
    IN_TRANSIT: 'In Transit',
    COMING_SOON: 'Coming Soon',
    RESERVED: 'Reserved',
    SOLD: 'Sold',
  };
  return labels[availability] || availability;
};

/**
 * Format distance for display
 * @param distanceInMeters - Distance in meters
 * @returns Formatted distance string (e.g., "25 mi")
 */
export const formatDistance = (distanceInMeters: number): string => {
  const miles = Math.round(distanceInMeters / 1609.34);
  return `${miles} mi`;
};

/**
 * Format price for display
 * @param price - Price in cents or dollars
 * @returns Formatted price string (e.g., "$69,900")
 */
export const formatPrice = (price: number): string => {
  if (!price) return '-';
  return formatLegoPrice(price, {
    locale: 'en-US',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
    fallback: '-',
  });
};
